<script setup lang="ts">
import type { UserLoggedOutSocketMessage } from '../../types'
import type * as Theme from '#theme'
import { computed, watch, useNotifications, useRoute, useXmppListener, useCookie, useReload, useCoreConfig, useUserInfo, navigateTo } from '#imports'

/**
 * Optional theme override. Takes precedence over `route.meta.theme` so
 * layouts (or pages rendering `<NuxtLayout>` directly) can drive the
 * global theme reactively — e.g. a playground that swaps themes via a
 * query param. Falls through to the route meta, then the configured
 * default.
 */
const props = defineProps<{
    theme?: Theme.Name
}>()

const { theme: { defaultTheme } } = useCoreConfig()
const theme = computed(() => props.theme || route.meta.theme || defaultTheme)

const route = useRoute()
const { clearNotificationsOnPageChange } = useNotifications()
const { userJustLoggedIn } = useUserInfo()

if (import.meta.client) {
    const reload = useReload()
    const userLoggedOutBySingleSessionRuleCookie = useCookie<string | undefined>('userForcedLoggedOut', { secure: true })

    useXmppListener({ kind: 'message', xmlns: 'cego:ForceReloadByUserId' }, async () => {
        await reload()
    })

    useXmppListener({ kind: 'message', xmlns: 'cego:UserLoggedOut' }, async (payload: UserLoggedOutSocketMessage) => {
        const { reason, logoutWasForced } = payload || {}

        if (reason === 'single_session_rule' && logoutWasForced && !userJustLoggedIn.value) {
            userLoggedOutBySingleSessionRuleCookie.value = 'single_session_rule'

            if (!route.meta.isPlaygame) {
                await reload()
            }
        }

        if (route.meta.isPlaygame) {
            return navigateTo({
                path: '/playgame/login',
                query: {
                    auth: 'login'
                }
            })
        }
    })
}

if (import.meta.client) {
    watch(() => route.path, () => {
        clearNotificationsOnPageChange()
    })
}
</script>

<template>
    <ThemeProvider :theme global>
        <slot />
    </ThemeProvider>
</template>
